import { DOCUMENT, inject, Injectable } from '@angular/core';
import { LoggingService } from '@athena/util-monitoring';
import { STORAGE_APP_PREFIX } from '../constants/storage.constant';

@Injectable({
  providedIn: 'root',
})
export class SessionStorageService {
  readonly #loggingService = inject(LoggingService);
  readonly #document = inject(DOCUMENT);

  /**
   * Build the prefixed key used for session storage.
   */
  private getKey(key: string): string {
    return `${STORAGE_APP_PREFIX}${key}`;
  }

  /**
   * Save a value to sessionStorage.
   * Automatically serializes non-string values.
   */
  save<T>(key: string, value: T): void {
    try {
      const prefixedKey = this.getKey(key);
      const serialized = typeof value === 'string' ? value : JSON.stringify(value);
      this.#document.defaultView?.sessionStorage.setItem(prefixedKey, serialized);
    } catch (error) {
      this.#loggingService.error(`Error saving to sessionStorage for key "${key}":`, error);
    }
  }

  /**
   * Retrieve and deserialize a value from sessionStorage.
   */
  get<T>(key: string): T | null {
    try {
      const prefixedKey = this.getKey(key);
      const value = this.#document.defaultView?.sessionStorage.getItem(prefixedKey) ?? null;
      if (value === null) return null;

      try {
        return JSON.parse(value) as T;
      } catch {
        return value as unknown as T;
      }
    } catch (error) {
      this.#loggingService.error(`Error retrieving from sessionStorage for key "${key}":`, error);
      return null;
    }
  }

  /**
   * Update an existing value in sessionStorage.
   * Returns false if the key does not exist.
   */
  update<T>(key: string, value: T): boolean {
    try {
      const prefixedKey = this.getKey(key);
      const storage = this.#document.defaultView?.sessionStorage;
      if (!storage) return false;

      if (storage.getItem(prefixedKey) === null) {
        return false;
      }

      this.save(key, value);
      return true;
    } catch (error) {
      this.#loggingService.error(`Error updating sessionStorage for key "${key}":`, error);
      return false;
    }
  }

  /**
   * Remove a specific key from sessionStorage.
   */
  remove(key: string): void {
    try {
      const prefixedKey = this.getKey(key);
      this.#document.defaultView?.sessionStorage.removeItem(prefixedKey);
    } catch (error) {
      this.#loggingService.error(`Error removing from sessionStorage for key "${key}":`, error);
    }
  }

  /**
   * Remove multiple keys from sessionStorage.
   */
  removeAll(keys: string[]): void {
    keys.forEach((key) => this.remove(key));
  }

  /**
   * Clear all data from sessionStorage.
   */
  clear(): void {
    try {
      this.#document.defaultView?.sessionStorage.clear();
    } catch (error) {
      this.#loggingService.error('Error clearing sessionStorage:', error);
    }
  }
}
